import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { 
  RefreshCw, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Calendar,
  ChevronRight,
  Bell,
  Timer,
  XCircle,
  Download,
  FileSpreadsheet,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DEADLINE_STATUS_CONFIG = {
  urgent: { 
    bg: "bg-red-100", 
    text: "text-red-700", 
    border: "border-red-200",
    icon: AlertTriangle,
    label: "Urgent"
  },
  approaching: { 
    bg: "bg-amber-100", 
    text: "text-amber-700", 
    border: "border-amber-200",
    icon: Clock,
    label: "Approaching"
  },
  pending: { 
    bg: "bg-blue-100", 
    text: "text-blue-700", 
    border: "border-blue-200",
    icon: Calendar,
    label: "Pending"
  },
  expired: { 
    bg: "bg-slate-100", 
    text: "text-slate-500", 
    border: "border-slate-200",
    icon: XCircle,
    label: "Expired"
  },
  not_applicable: { 
    bg: "bg-green-100", 
    text: "text-green-700", 
    border: "border-green-200",
    icon: CheckCircle,
    label: "N/A"
  }
};

const DeadlineBadge = ({ status, daysRemaining }) => {
  const config = DEADLINE_STATUS_CONFIG[status] || DEADLINE_STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {daysRemaining !== null && daysRemaining !== undefined ? (
        status === "expired" ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d left`
      ) : (
        config.label
      )}
    </span>
  );
};

export const OppositionDeadlines = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [recalculating, setRecalculating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, deadlinesRes] = await Promise.all([
        axios.get(`${API}/opposition-deadlines/summary`),
        axios.get(`${API}/opposition-deadlines`, {
          params: {
            status: filter !== "all" ? filter : undefined,
            limit: 100
          }
        })
      ]);
      
      setSummary(summaryRes.data);
      setDeadlines(deadlinesRes.data.deadlines);
    } catch (error) {
      console.error("Error fetching deadlines:", error);
      toast.error("Failed to load opposition deadlines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await axios.post(`${API}/opposition-deadlines/recalculate`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to recalculate deadlines");
    } finally {
      setRecalculating(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("deadline_status", filter);
      
      const response = await axios.get(`${API}/opposition-deadlines/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'deadlines_export.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Deadlines exported as CSV");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export deadlines");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (loading && !summary) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Opposition Deadlines
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track and manage opposition windows for conflicting trademarks
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportCSV}
            variant="outline"
            size="sm"
            disabled={exporting || deadlines.length === 0}
            data-testid="export-deadlines-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
          <Button
            onClick={handleRecalculate}
            variant="outline"
            size="sm"
            disabled={recalculating}
            data-testid="recalculate-deadlines-btn"
          >
            <Timer className={`w-4 h-4 mr-2 ${recalculating ? "animate-spin" : ""}`} />
            Recalculate
          </Button>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="refresh-deadlines-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="swiss-card p-4"
            data-testid="total-deadlines-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_with_deadlines}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Active</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`swiss-card p-4 ${summary.urgent_count > 0 ? 'ring-2 ring-red-200' : ''}`}
            data-testid="urgent-deadlines-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.urgent_count}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Urgent (≤7d)</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="swiss-card p-4"
            data-testid="approaching-deadlines-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{summary.approaching_count}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Approaching</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="swiss-card p-4"
            data-testid="pending-deadlines-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Timer className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.pending_count}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Pending (&gt;30d)</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="swiss-card p-4"
            data-testid="expired-deadlines-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-400">{summary.expired_count}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Expired</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-sm text-slate-500">Filter:</span>
        {["all", "urgent", "approaching", "pending", "expired"].map((status) => (
          <Button
            key={status}
            variant={filter === status ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(status)}
            data-testid={`filter-${status}-btn`}
          >
            {status === "all" ? "All Deadlines" : status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {/* Deadlines List */}
      <div className="swiss-card overflow-hidden" data-testid="deadlines-list">
        {deadlines.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-medium text-slate-900 mb-2">No Opposition Deadlines</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              {filter !== "all" 
                ? `No ${filter} deadlines found. Try a different filter.`
                : "Opposition deadlines will appear here for trademarks with 'Published' status. Set publication dates on alerts to track deadlines."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {deadlines.map((deadline, index) => {
              const config = DEADLINE_STATUS_CONFIG[deadline.deadline_status] || DEADLINE_STATUS_CONFIG.pending;
              
              return (
                <motion.div
                  key={deadline.alert_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                    deadline.deadline_status === 'urgent' ? 'bg-red-50/50' : ''
                  }`}
                  onClick={() => navigate(`/alerts/${deadline.alert_id}`)}
                  data-testid={`deadline-${deadline.alert_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <DeadlineBadge 
                          status={deadline.deadline_status} 
                          daysRemaining={deadline.days_remaining} 
                        />
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          deadline.overall_risk_level === 'high' ? 'bg-red-100 text-red-700' :
                          deadline.overall_risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {deadline.overall_risk_level.toUpperCase()} RISK
                        </span>
                        {deadline.reminder_sent && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Bell className="w-3 h-3" /> Reminded
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-400 uppercase mb-1">Your Trademark</p>
                          <p className="font-medium text-slate-900">{deadline.my_trademark_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 uppercase mb-1">Conflicting Application</p>
                          <p className="font-medium text-slate-900">{deadline.conflicting_trademark_name}</p>
                          <p className="text-xs text-slate-500">{deadline.conflicting_trademark_application_number}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-xs text-slate-400 uppercase">Deadline</p>
                          <p className={`font-mono font-medium ${config.text}`}>
                            {formatDate(deadline.opposition_deadline)}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">About Opposition Deadlines</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• In the UK, you have <strong>2 months</strong> from the publication date to file an opposition</li>
          <li>• <span className="text-red-600 font-medium">Urgent</span>: 7 days or less remaining</li>
          <li>• <span className="text-amber-600 font-medium">Approaching</span>: 8-30 days remaining</li>
          <li>• Email reminders are sent at 30, 14, and 7 days before the deadline</li>
          <li>• Click on a deadline to view the full alert details and take action</li>
        </ul>
      </div>
    </div>
  );
};
